// src/Pages/Profile/StaffProfile.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  Avatar,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  OutlinedInput,
  FormHelperText,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import AppSnackbar from "../Components/Snackbar/snackbar";
import apiEndpoints from "../apiconfig";

const StaffProfile = () => {
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const [qualificationOptions, setQualificationOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [specializationOptions, setSpecializationOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);

  const classMap = useMemo(() => {
    const map = {};
    classOptions.forEach((c) => {
      map[c.guid] = c.name;
    });
    return map;
  }, [classOptions]);
  // -------- FETCH STAFF + DROPDOWNS --------
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const authHeader = { Authorization: `Bearer ${token}` };

    let staffData = null;

    // 1️⃣ LOAD PROFILE
    fetch(`${apiEndpoints.staffProfile}?action=get`, { headers: authHeader })
      .then((r) => r.json())
      .then((profile) => {
        if (!profile.status) throw new Error();
        staffData = profile.data;

        // set basic profile + specialization
        setForm((prev) => ({
          ...prev,
          ...staffData,
          specializations: Array.isArray(staffData.specialization)
            ? staffData.specialization
            : JSON.parse(staffData.specialization || "[]"),
          assignedClass: staffData.assigned_class || "",
        }));

        if (staffData.profile_photo) {
          setPhotoPreview(`${staffData.profile_photo}#${Date.now()}`);
        }

        // 2️⃣ LOAD DROPDOWNS
        return Promise.all([
          fetch(`${apiEndpoints.dropdown}?type=qualification`, {
            headers: authHeader,
          }).then((r) => r.json()),
          fetch(`${apiEndpoints.dropdown}?type=role`, {
            headers: authHeader,
          }).then((r) => r.json()),
          fetch(`${apiEndpoints.dropdown}?type=specialization`, {
            headers: authHeader,
          }).then((r) => r.json()),
          fetch(
            `${apiEndpoints.dropdown}?type=class&branch_guid=${staffData.branch_guid}`,
            { headers: authHeader },
          ).then((r) => r.json()),
        ]);
      })
      .then(([q, r, s, c]) => {
        setQualificationOptions(q.data || []);
        setRoleOptions(r.data || []);
        setSpecializationOptions(s.data || []);
        setClassOptions(c.data || []);

        // 3️⃣ MAP ASSIGNED CLASS GUID → NAME (NOW SAFE ✅)
      })
      .catch(() =>
        setSnack({
          open: true,
          message: "Unable to load profile",
          severity: "error",
        }),
      );
  }, []);

  // -------- CHANGE HANDLERS --------
  const handleChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const handleAssignedClassChange = (e) =>
    handleChange("assignedClasses", e.target.value);

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setForm((prev) => ({ ...prev, profile_photo: reader.result }));
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // -------- VALIDATION --------
  const validate = () => {
    const e = {};
    if (!photoPreview) e.profilePhoto = "Profile photo is required";
    if (!form.first_name) e.first_name = "First name is required";
    if (!form.email) e.email = "Email is required";
    if (!form.mobile) e.mobile = "Mobile number is required";
    if (!form.role) e.role = "Select a valid role";
    if (!form.specializations?.length)
      e.specializations = "Add at least one specialization";
    if (!form.assignedClass) e.assignedClass = "Select a class";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // -------- SUBMIT --------
  const handleSubmit = () => {
    if (!validate()) return;

    const payload = {
      ...form,
      specialization: JSON.stringify(form.specializations),
      assigned_class: form.assignedClass, // ✅ GUID only
    };

    fetch(`${apiEndpoints.staffProfile}?action=update`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json())
      .then((res) => {
        setSnack({
          open: true,
          message: res.message,
          severity: res.status ? "success" : "error",
        });
        if (res.status && res.data?.profile_photo) {
          setPhotoPreview(`${res.data.profile_photo}#${Date.now()}`);
        }
        if (res.status)
          localStorage.setItem(
            "user",
            JSON.stringify({
              ...payload,
              profile_photo: res.data.profile_photo,
            }),
          );
      });
  };

  // -------- UI --------
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        Staff Profile
      </Typography>

      <Paper
        sx={{
          p: 3,
          borderRadius: 3,
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.15) 100%)",
          backdropFilter: "blur(16px)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
        }}
      >
        <Grid container spacing={3}>
          {/* PROFILE PHOTO SECTION MOVED TO TOP */}
          <Grid item xs={12} textAlign="center">
            <Avatar
              src={photoPreview}
              sx={{
                width: 120,
                height: 120,
                mx: "auto",
                mb: 1,
                border: errors.profilePhoto ? "2px solid red" : "",
              }}
            />
            <Button component="label" variant="contained">
              Upload Photo
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
              />
            </Button>
            <FormHelperText error>{errors.profilePhoto}</FormHelperText>
          </Grid>

          {/* FIRST NAME */}
          <Grid item xs={12} md={6}>
            <TextField
              label="First Name *"
              fullWidth
              value={form.first_name || ""}
              onChange={(e) => handleChange("first_name", e.target.value)}
              error={Boolean(errors.first_name)}
              helperText={errors.first_name}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderColor: errors.first_name && "red",
                },
              }}
            />
          </Grid>

          {/* LAST NAME */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Last Name"
              fullWidth
              value={form.last_name || ""}
              onChange={(e) => handleChange("last_name", e.target.value)}
            />
          </Grid>

          {/* EMAIL */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Email *"
              fullWidth
              value={form.email || ""}
              onChange={(e) => handleChange("email", e.target.value)}
              error={Boolean(errors.email)}
              helperText={errors.email}
            />
          </Grid>

          {/* MOBILE */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Mobile *"
              fullWidth
              value={form.mobile || ""}
              onChange={(e) => handleChange("mobile", e.target.value)}
              error={Boolean(errors.mobile)}
              helperText={errors.mobile}
            />
          </Grid>

          {/* ROLE */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={Boolean(errors.role)}>
              <Autocomplete
                options={roleOptions.map((o) => o.name)}
                value={form.role || ""}
                onChange={(_, v) => handleChange("role", v)}
                renderInput={(p) => <TextField {...p} label="Role *" />}
              />
              <FormHelperText>{errors.role}</FormHelperText>
            </FormControl>
          </Grid>

          {/* SPECIALIZATIONS */}
          <Grid item xs={12}>
            <Autocomplete
              multiple
              freeSolo
              options={specializationOptions.map((o) => o.name)}
              value={form.specializations || []}
              onChange={(_, v) => handleChange("specializations", v)}
              renderInput={(p) => (
                <TextField
                  {...p}
                  label="Specializations *"
                  error={Boolean(errors.specializations)}
                  helperText={errors.specializations}
                />
              )}
            />
          </Grid>

          {/* ASSIGNED CLASS */}
          <Grid item xs={12}>
            <FormControl
              fullWidth
              error={Boolean(errors.assignedClass)}
              disabled={true}
            >
              <InputLabel>Assigned Class *</InputLabel>
              <Select
                value={form.assignedClass || ""}
                onChange={(e) => handleChange("assignedClass", e.target.value)}
                input={<OutlinedInput label="Assigned Class *" />}
              >
                {classOptions.map((c) => (
                  <MenuItem key={c.guid} value={c.guid}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.assignedClass}</FormHelperText>
            </FormControl>
          </Grid>

          {/* ADDRESS */}
          <Grid item xs={12}>
            <TextField
              label="Address"
              fullWidth
              multiline
              rows={2}
              value={form.address || ""}
              onChange={(e) => handleChange("address", e.target.value)}
            />
          </Grid>

          {/* SAVE BUTTON */}
          <Grid item xs={12}>
            <Button variant="contained" fullWidth onClick={handleSubmit}>
              Save Profile
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <AppSnackbar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack({ ...snack, open: false })}
      />
    </Box>
  );
};

export default StaffProfile;
